import {
  ADVICE_NAMES,
  Advice,
  AdviceChanges,
  InsuranceAdvice,
  PurchaseRecommendation,
  SinglePayment,
  UserAuthorizationLevel,
  YearlyPayment,
} from "Store/advice/interfaces";
import { getCorrectPayment, isChecked } from "Store/advice/utils";
import {
  getCorrectPaymentValue,
  replaceUserSupportNumberInText,
} from "../../AdviceCards/utils";

import { VALUE_TYPE } from "Components/NumberValue";
import { formatToLocaleString } from "Formatting/formatNumber";
import { getCorrectAdviceValues } from "Store/advice/productPriceRequestMappers";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { isDefined } from "Utils/functions";
import { AdvicePriceFormValues } from "../../AdviceCards/interfaces";
import { generateUnavailableMessage } from "../generateUnavailableMessage";

export enum AdviceDiffType {
  NO_DIFF,
  CHECKED_DIFF,
  SLIDER_DIFF,
  INFO,
  UNAVAILABLE,
}

export interface DiffMessageValues {
  insuranceProduct: ADVICE_NAMES;
  diffType: AdviceDiffType;
  message: string;
  additionalMessages: string[];
}

export const generateDiffMessage = (
  isAdvisor: boolean,
  partnerId: string,
  changedByCustomer: AdviceChanges,
  insurance: {
    name: ADVICE_NAMES;
    title: string;
  },
  adviceMessagesText: any,
  lang: string,
  insuranceAdvice?: InsuranceAdvice,
  // eslint-disable-next-line sonarjs/cognitive-complexity
): DiffMessageValues | null => {
  const { insuranceTypes, paymentDiff } = adviceMessagesText.adviceMessages;
  const insuranceType = insuranceTypes[insurance.title][lang];
  const valueType =
    insurance.name === INSURANCE_NAME_VALUE.DISABILITY_FLEX
      ? ` ${VALUE_TYPE.NOK_YEAR}`
      : ` ${VALUE_TYPE.NOK}`;
  if (!(insuranceAdvice && isChecked(insuranceAdvice))) {
    const message = generateUnavailableMessage(
      isAdvisor,
      insurance,
      adviceMessagesText.adviceUnavailableMessages,
      lang,
      insuranceAdvice,
    )?.message;
    if (message) {
      const productCheckedToPurchase =
        changedByCustomer[insurance.name]?.productCheckedToPurchase;
      const sliderValue = changedByCustomer[insurance.name]?.sliderValue;
      const { youHaveChosen, withCoverage } = paymentDiff;
      return {
        insuranceProduct: insurance.name,
        diffType: productCheckedToPurchase
          ? AdviceDiffType.CHECKED_DIFF
          : AdviceDiffType.UNAVAILABLE,
        message: productCheckedToPurchase
          ? `${youHaveChosen[lang]}${insuranceType}${
              withCoverage[lang]
            }${formatToLocaleString(sliderValue)}${valueType}. ${message}`
          : message,
        additionalMessages: [],
      };
    }
    return null;
  }

  let diffType = getAdviceDiffType(
    insurance.name,
    insuranceAdvice,
    changedByCustomer,
  );
  const { customerInsuredSum, adviceValue } = checkAdviceAndCustomerDiff(
    insurance.name,
    insuranceAdvice,
    changedByCustomer,
  );

  let additionalMessages: string[] = [];

  const purchaseFromAdvisor = addPurchaseFromAdvisorMessages(
    partnerId,
    insurance,
    adviceMessagesText.adviceUnavailableMessages,
    lang,
    insuranceAdvice,
  );
  const maxPayoutCustomerText = purchaseFromAdvisor.maxPayoutCustomerText;
  additionalMessages = additionalMessages.concat(
    purchaseFromAdvisor.additionalMessages,
  );

  const disabilityOneTimePaymentText = getDisabilityOneTimePaymentText(
    changedByCustomer,
    insurance,
    insuranceAdvice,
    adviceMessagesText.adviceUnavailableMessages.purchaseFromAdvisor,
    lang,
  );

  const insuranceWithCoverage = `${insuranceType}${paymentDiff.withCoverage[lang]}`;
  let diffText = "";
  if (diffType === AdviceDiffType.CHECKED_DIFF) {
    diffText = `${paymentDiff.weAlsoAdvice[lang]}${insuranceType}${
      paymentDiff.reason[insurance.title][lang]
    }`;
  } else if (
    diffType === AdviceDiffType.SLIDER_DIFF ||
    (customerInsuredSum !== undefined && customerInsuredSum !== adviceValue)
  ) {
    diffText = `${
      paymentDiff.youHaveChosen[lang]
    }${insuranceWithCoverage}${formatToLocaleString(
      customerInsuredSum,
    )}${valueType}${paymentDiff.weAdviced[lang]}${formatToLocaleString(
      adviceValue,
    )}${valueType}${maxPayoutCustomerText}${disabilityOneTimePaymentText}`;
  } else {
    const toFollowAdvice =
      purchaseFromAdvisor.additionalMessages.length > 0
        ? ""
        : paymentDiff.toFollowAdvice[lang];
    diffText = `${
      paymentDiff.youHaveChosen[lang]
    }${toFollowAdvice}${insuranceWithCoverage}${formatToLocaleString(
      adviceValue,
    )}${valueType}${maxPayoutCustomerText}${disabilityOneTimePaymentText}`;
  }

  const unavailableMessage = generateUnavailableMessage(
    isAdvisor,
    insurance,
    adviceMessagesText.adviceUnavailableMessages,
    lang,
    insuranceAdvice,
  )?.message;
  if (unavailableMessage) {
    additionalMessages.push(unavailableMessage);
  }

  if (purchaseFromAdvisor.additionalMessages.length > 0) {
    diffType = AdviceDiffType.SLIDER_DIFF;
  }

  return {
    insuranceProduct: insurance.name,
    diffType,
    message: diffText,
    additionalMessages,
  };
};

const checkAdviceAndCustomerDiff = (
  insuranceName: ADVICE_NAMES,
  insuranceAdvice: InsuranceAdvice,
  changedByCustomer: AdviceChanges,
): {
  valid: boolean;
  customerInsuredSum: number;
  adviceValue: number;
} => {
  const { adviceValue, minAdviceValue, maxAdviceValue } =
    getCorrectAdviceValues(getCorrectPayment(insuranceAdvice));
  const customerInsuredSum: number | undefined =
    changedByCustomer[insuranceName]?.sliderValue;
  const valuesAreNotPresent =
    minAdviceValue === null ||
    maxAdviceValue === null ||
    !isDefined(customerInsuredSum);
  if (
    valuesAreNotPresent ||
    (!valuesAreNotPresent &&
      minAdviceValue <= customerInsuredSum &&
      customerInsuredSum <= maxAdviceValue)
  ) {
    return { valid: true, customerInsuredSum, adviceValue };
  }
  return { valid: false, customerInsuredSum, adviceValue };
};

const getAdviceDiffType = (
  insuranceName: ADVICE_NAMES,
  insuranceAdvice: InsuranceAdvice,
  changedByCustomer: AdviceChanges,
): AdviceDiffType => {
  const productCheckedToPurchase =
    changedByCustomer[insuranceName]?.productCheckedToPurchase;
  const notCheckedAsAdviced =
    productCheckedToPurchase !== undefined &&
    isChecked(insuranceAdvice) !== productCheckedToPurchase;
  if (notCheckedAsAdviced) {
    return AdviceDiffType.CHECKED_DIFF;
  }
  const checkedToPurchase = notCheckedAsAdviced
    ? productCheckedToPurchase
    : isChecked(insuranceAdvice);
  const adviceAndCustomerDiff = checkAdviceAndCustomerDiff(
    insuranceName,
    insuranceAdvice,
    changedByCustomer,
  );
  if (checkedToPurchase && !adviceAndCustomerDiff.valid) {
    return AdviceDiffType.SLIDER_DIFF;
  }
  return AdviceDiffType.NO_DIFF;
};

export const addPurchaseFromAdvisorMessages = (
  partnerId: string,
  insurance: {
    name: ADVICE_NAMES;
    title: string;
  },
  adviceMessagesText: any,
  lang: string,
  insuranceAdvice: InsuranceAdvice,
): { maxPayoutCustomerText: string; additionalMessages: string[] } => {
  const purchaseFromAdvisorText = adviceMessagesText.purchaseFromAdvisor;
  const maxPayoutText = purchaseFromAdvisorText.maxPayoutText[lang];
  let maxPayoutCustomerText = ".";
  let additionalMessage = "";
  const shouldPurchaseFromAdvisor =
    getCorrectPayment(insuranceAdvice)?.recommendationConfiguration
      .recommendation === PurchaseRecommendation.PURCHASE_FROM_ADVISOR;
  const getRecommended = (payment?: SinglePayment | YearlyPayment): string =>
    `${formatToLocaleString(
      getCorrectPaymentValue(UserAuthorizationLevel.PRODUCT_MANAGER, payment),
    )} ${VALUE_TYPE.NOK}`;
  const { oneTime, yearly, andOneTime } = purchaseFromAdvisorText.payment;
  if (insurance.name === INSURANCE_NAME_VALUE.DISABILITY_FLEX) {
    const shouldPurchaseFromAdvisorOneTimePayment =
      insuranceAdvice?.singlePayment.recommendationConfiguration
        .recommendation === PurchaseRecommendation.PURCHASE_FROM_ADVISOR;
    if (shouldPurchaseFromAdvisor && shouldPurchaseFromAdvisorOneTimePayment) {
      maxPayoutCustomerText = maxPayoutText;
      additionalMessage = `${yearly[lang].replace(
        "{{value}}",
        getRecommended(insuranceAdvice.yearlyPayment),
      )} ${andOneTime[lang].replace(
        "{{value}}",
        getRecommended(insuranceAdvice.singlePayment),
      )}`;
    } else if (
      shouldPurchaseFromAdvisor &&
      !shouldPurchaseFromAdvisorOneTimePayment
    ) {
      maxPayoutCustomerText = maxPayoutText;
      additionalMessage = `${yearly[lang].replace(
        "{{value}}",
        getRecommended(insuranceAdvice.yearlyPayment),
      )}`;
    } else if (
      !shouldPurchaseFromAdvisor &&
      shouldPurchaseFromAdvisorOneTimePayment
    ) {
      additionalMessage = `${oneTime[lang].replace(
        "{{value}}",
        getRecommended(insuranceAdvice.singlePayment),
      )}`;
    }
  } else if (shouldPurchaseFromAdvisor) {
    maxPayoutCustomerText = maxPayoutText;
    additionalMessage = `${oneTime[lang].replace(
      "{{value}}",
      getRecommended(insuranceAdvice.singlePayment),
    )}`;
  }
  return {
    maxPayoutCustomerText,
    additionalMessages:
      additionalMessage !== ""
        ? [
            getPurchaseFromAdvisorDiffMessage(
              partnerId,
              insurance.name,
              additionalMessage,
              purchaseFromAdvisorText,
              lang,
              insuranceAdvice,
            ),
          ]
        : [],
  };
};

const getDisabilityOneTimePaymentText = (
  changedByCustomer: AdviceChanges,
  insurance: {
    name: ADVICE_NAMES;
    title: string;
  },
  insuranceAdvice: InsuranceAdvice,
  purchaseFromAdvisorText: any,
  lang: string,
): string => {
  if (
    insurance.name === INSURANCE_NAME_VALUE.DISABILITY_FLEX &&
    changedByCustomer[insurance.name]?.additionalData
      ?.disabilityOneTimePaymentChecked
  ) {
    return purchaseFromAdvisorText.optionalOneTimePayment[lang].replace(
      "{{value}}",
      `${formatToLocaleString(
        getCorrectAdviceValues(insuranceAdvice.singlePayment).adviceValue,
      )} ${VALUE_TYPE.NOK}`,
    );
  }
  return "";
};

const getPurchaseFromAdvisorDiffMessage = (
  partnerId: string,
  insuranceName: ADVICE_NAMES,
  customText: string,
  purchaseFromAdvisorText: any,
  lang: string,
  advice?: InsuranceAdvice,
): string => {
  if (advice) {
    const weAdviceShort: string = replaceUserSupportNumberInText(
      partnerId,
      purchaseFromAdvisorText.userSupportNumber,
      purchaseFromAdvisorText.weAdviceShort[lang],
      lang,
    );
    if (
      insuranceName === INSURANCE_NAME_VALUE.DISABILITY_FLEX &&
      advice.singlePayment.recommendationConfiguration.recommendation ===
        PurchaseRecommendation.PURCHASE_FROM_ADVISOR
    ) {
      return weAdviceShort.replace(
        "{{payment}}",
        customText.replace(VALUE_TYPE.NOK_YEAR, VALUE_TYPE.NOK),
      );
    }
    if (
      getCorrectPayment(advice).recommendationConfiguration.recommendation ===
      PurchaseRecommendation.PURCHASE_FROM_ADVISOR
    ) {
      return weAdviceShort.replace("{{payment}}", customText);
    }
  }
  return "";
};

export const checkShouldPurchaseAdviceFromAdvisor = (
  isAdvisor: boolean,
  adviceResult: Advice,
  formValues: Partial<AdvicePriceFormValues>,
): boolean => {
  const shouldPurchaseDisabilityInsuranceFromAdvisor =
    checkShouldPurchaseFromAdvisor(
      isAdvisor,
      adviceResult.advice,
      INSURANCE_NAME_VALUE.DISABILITY_FLEX,
      formValues,
    );
  const shouldPurchaseDeathInsuranceFromAdvisor =
    checkShouldPurchaseFromAdvisor(
      isAdvisor,
      adviceResult.advice,
      INSURANCE_NAME_VALUE.DEATH,
      formValues,
    );
  const shouldPurchaseCriticalInsuranceFromAdvisor =
    checkShouldPurchaseFromAdvisor(
      isAdvisor,
      adviceResult.advice,
      INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
      formValues,
    );

  return (
    shouldPurchaseDisabilityInsuranceFromAdvisor ||
    shouldPurchaseDeathInsuranceFromAdvisor ||
    shouldPurchaseCriticalInsuranceFromAdvisor
  );
};

const checkShouldPurchaseFromAdvisor = (
  isAdvisor: boolean,
  advice: Record<ADVICE_NAMES, InsuranceAdvice>,
  insuranceName: ADVICE_NAMES,
  formValues: Partial<AdvicePriceFormValues>,
): boolean => {
  switch (insuranceName) {
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      return checkShouldPurchaseDisabilityFromAdvisor(
        isAdvisor,
        advice[insuranceName],
        formValues,
      );
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
      return checkShouldPurchaseCriticalIllnessFromAdvisor(
        isAdvisor,
        advice[insuranceName],
        formValues,
      );
    case INSURANCE_NAME_VALUE.DEATH:
      return checkShouldPurchaseDeathFromAdvisor(
        isAdvisor,
        advice[insuranceName],
        formValues,
      );
  }
};

const checkShouldPurchaseDisabilityFromAdvisor = (
  isAdvisor: boolean,
  insuranceAdvice: InsuranceAdvice,
  {
    disabilityCheckedToPurchase,
    disabilityInsuredSumValue,
    disabilityOneTimePayment,
  }: Partial<AdvicePriceFormValues>,
): boolean => {
  if (
    isAdvisor ||
    !isDefined(disabilityCheckedToPurchase) ||
    !isDefined(disabilityInsuredSumValue) ||
    !disabilityCheckedToPurchase
  ) {
    return false;
  }

  const oneTimePaymentRecommendation =
    insuranceAdvice.singlePayment.recommendationConfiguration.recommendation;
  const yearlyPaymentRecommendation =
    insuranceAdvice.yearlyPayment?.recommendationConfiguration.recommendation;
  const maxPayoutCustomer =
    insuranceAdvice.yearlyPayment?.restrictions?.maxPayouts.CUSTOMER ?? 0;

  return (
    (oneTimePaymentRecommendation ===
      PurchaseRecommendation.PURCHASE_FROM_ADVISOR &&
      disabilityOneTimePayment === false) ||
    (yearlyPaymentRecommendation ===
      PurchaseRecommendation.PURCHASE_FROM_ADVISOR &&
      disabilityInsuredSumValue === maxPayoutCustomer)
  );
};

const checkShouldPurchaseCriticalIllnessFromAdvisor = (
  isAdvisor: boolean,
  insuranceAdvice: InsuranceAdvice,
  {
    criticalIllnessCheckedToPurchase,
    criticalIllnessInsuredSumValue,
  }: Partial<AdvicePriceFormValues>,
): boolean => {
  if (
    isAdvisor ||
    !isDefined(criticalIllnessCheckedToPurchase) ||
    !isDefined(criticalIllnessInsuredSumValue) ||
    !criticalIllnessCheckedToPurchase
  ) {
    return false;
  }

  const { recommendation } =
    insuranceAdvice.singlePayment.recommendationConfiguration;
  const maxPayoutCustomer =
    insuranceAdvice.singlePayment.restrictions?.maxPayouts.CUSTOMER ?? 0;

  return (
    recommendation === PurchaseRecommendation.PURCHASE_FROM_ADVISOR &&
    criticalIllnessInsuredSumValue === maxPayoutCustomer
  );
};

const checkShouldPurchaseDeathFromAdvisor = (
  isAdvisor: boolean,
  insuranceAdvice: InsuranceAdvice,
  {
    deathCheckedToPurchase,
    deathInsuredSumValue,
  }: Partial<AdvicePriceFormValues>,
): boolean => {
  if (
    isAdvisor ||
    !isDefined(deathCheckedToPurchase) ||
    !isDefined(deathInsuredSumValue) ||
    !deathCheckedToPurchase
  ) {
    return false;
  }

  const { recommendation } =
    insuranceAdvice.singlePayment.recommendationConfiguration;
  const maxPayoutCustomer =
    insuranceAdvice.singlePayment.restrictions?.maxPayouts.CUSTOMER ?? 0;

  return (
    recommendation === PurchaseRecommendation.PURCHASE_FROM_ADVISOR &&
    deathInsuredSumValue === maxPayoutCustomer
  );
};
